import { narrowAttachments } from './attachments';
import type { MessageAttachment } from '../types/api';
import type { ChatMessageEvent } from '../types/chat';

/**
 * 방 전체 첨부 병합 (chat-room-wide-attachments — 2026-07-10).
 *
 * 우측 미디어 패널은 원래 `chatStore.messagesByRoom` — 즉 **페이지네이션 창** — 에서만 첨부를
 * 모아서, "이전 대화 불러오기" 를 누르기 전에는 과거 첨부가 보이지 않았다.
 *
 * 이제 서버의 `GET /chat/rooms/:id/attachments` (방 전체) 결과를 쓰되, **스토어에서 뽑은 것과
 * 합친다.** 서버 응답은 패널을 열 때 한 번 받으므로, 그 뒤 SSE 로 도착한 새 첨부는 스토어에만
 * 있다. 둘을 합쳐야 "전체 대화 + 실시간" 이 모두 성립한다.
 *
 * 서버 조회가 실패해도 스토어 쪽만으로 기존 동작(로드된 창)을 유지한다 — 회귀 없음.
 *
 * 정렬: commitSeq 내림차순(최신 우선). 같은 fileId 는 1회만(더 최신 commitSeq 채택).
 */

/** 서버 `items[]` 한 건 — 첨부 원본 + 어느 메시지에서 왔는지. */
export interface RoomAttachmentItem extends MessageAttachment {
  messageId: string;
  commitSeq: string;
  senderId: string | null;
  createdAt: string;
}

export interface RoomAttachmentsResponse {
  items: RoomAttachmentItem[];
  nextCursor: string | null;
}

/** commitSeq 는 bigint 문자열이라 Number 로 비교하면 정밀도를 잃는다 → 길이 우선 문자열 비교. */
export function compareCommitSeqDesc(a: string, b: string): number {
  if (a.length !== b.length) return b.length - a.length;
  return b < a ? -1 : b > a ? 1 : 0;
}

/**
 * 서버 조회분 + 스토어 로드분을 fileId 로 중복 제거해 최신순 정렬.
 *
 * @param serverItems 방 전체 조회 결과(실패 시 빈 배열)
 * @param messages    현재 스토어에 로드된 메시지(실시간 반영분 포함)
 */
export function mergeRoomAttachments(
  serverItems: readonly RoomAttachmentItem[],
  messages: readonly ChatMessageEvent[] | undefined,
): MessageAttachment[] {
  const byFileId = new Map<string, { att: MessageAttachment; commitSeq: string }>();

  const put = (att: MessageAttachment, commitSeq: string) => {
    const prev = byFileId.get(att.fileId);
    // 같은 파일이 여러 메시지에 있으면 더 최신 메시지 기준으로 배치한다.
    if (!prev || compareCommitSeqDesc(commitSeq, prev.commitSeq) < 0) {
      byFileId.set(att.fileId, { att, commitSeq });
    }
  };

  for (const it of serverItems) put(it, it.commitSeq);

  for (const m of messages ?? []) {
    if (m.isDeleted) continue; // 톰스톤은 첨부 블랭크지만 방어적으로 건너뛴다.
    for (const a of narrowAttachments(m.attachments)) put(a, m.commitSeq);
  }

  return [...byFileId.values()]
    .sort((x, y) => compareCommitSeqDesc(x.commitSeq, y.commitSeq))
    .map((v) => v.att);
}

/* ────────────────────────────────────────────────────────────────────────────
 * chat-media-date-groups (2026-08-07) — 일자 그룹·정렬·검색용 확장.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * 출처 메타를 보존한 첨부. `mergeRoomAttachments` 는 반환 타입이 `MessageAttachment[]` 라
 * `createdAt`/`senderId`/`messageId` 를 **타입에서도 런타임에서도** 버린다(스토어 경로는 애초에
 * `narrowAttachments` 를 거쳐 첨부 JSON 만 남는다). 일자 그룹핑은 `createdAt` 이 있어야 하므로
 * 메타를 유지하는 별도 표현이 필요하다.
 */
export interface DatedAttachment extends MessageAttachment {
  messageId: string | null;
  commitSeq: string;
  senderId: string | null;
  /** ISO 문자열. 누락·무효면 '' — 호출측이 '날짜 미상' 버킷으로 보낸다(드롭 금지). */
  createdAt: string;
}

/**
 * `mergeRoomAttachments` 의 메타 보존판.
 *
 * ⚠ **기존 함수를 이 함수 위에 재구현하지 않는다.** 동작이 보존되더라도 기존 함수 본문을 다시 쓰는 것은
 * 프로젝트의 "소스 회귀 금지 · additive 우선" 규칙에 어긋나고, 이미 통과 중인
 * `room-attachments.test.ts` 의 단언 대상을 흔든다. 아래 ~20줄 중복은 그 대가로 의도적으로 감수한 것.
 *
 * 의미론은 원본과 동일하다 — `fileId` 중복 제거(더 최신 `commitSeq` 채택), 톰스톤 skip,
 * `compareCommitSeqDesc` 최신순 정렬. 차이는 출처 메타를 함께 들고 나온다는 점뿐이다.
 * 서버 항목은 그대로, 스토어 항목은 소유 메시지에서 `id`/`senderId`/`createdAt` 을 백필한다.
 */
export function mergeRoomAttachmentsDated(
  serverItems: readonly RoomAttachmentItem[],
  messages: readonly ChatMessageEvent[] | undefined,
): DatedAttachment[] {
  const byFileId = new Map<string, DatedAttachment>();

  const put = (item: DatedAttachment) => {
    const prev = byFileId.get(item.fileId);
    if (!prev || compareCommitSeqDesc(item.commitSeq, prev.commitSeq) < 0) {
      byFileId.set(item.fileId, item);
    }
  };

  for (const it of serverItems) {
    put({ ...it, messageId: it.messageId, commitSeq: it.commitSeq, senderId: it.senderId, createdAt: it.createdAt ?? '' });
  }

  for (const m of messages ?? []) {
    if (m.isDeleted) continue; // 톰스톤은 첨부 블랭크지만 방어적으로 건너뛴다.
    for (const a of narrowAttachments(m.attachments)) {
      put({
        ...a,
        messageId: m.id ?? null,
        commitSeq: m.commitSeq,
        senderId: m.senderId ?? null,
        createdAt: m.createdAt ?? '',
      });
    }
  }

  return [...byFileId.values()].sort((x, y) => compareCommitSeqDesc(x.commitSeq, y.commitSeq));
}
